import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, forwardRef } from "react";
import { useAppDispatch } from "@/store/hooks";
import { useParams } from "react-router-dom";
import {
  resetAmountOffOrder,
  resetAmountOffProducts,
  resetBuyXGetY,
  resetFreeShipping,
} from "@/store/slices/discount.slice";

// Define TypeScript type for discountTypes
type DiscountType = {
  title: string;
  description: string;
  icon: string;
  link: string;
};

const CreateDiscounts = forwardRef<HTMLButtonElement>((props, ref) => {
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();

  const discountTypes: DiscountType[] = [
    {
      title: "Amount Off Products",
      description: "Discount specific products or collections of products",
      icon: "/assets/icons/receipt_tax.svg",
      link: `/${storeType}/${storeId}/coupons/amount-off-products`,
    },
    {
      title: "Buy X Get Y",
      description: "Discount products based on a customer's purchase",
      icon: "/assets/icons/receipt_tax.svg",
      link: `/${storeType}/${storeId}/coupons/buy-x-get-y`,
    },
    {
      title: "Amount Off Order",
      description: "Discount the total order amount",
      icon: "/assets/icons/archive.svg",
      link: `/${storeType}/${storeId}/coupons/amount-off-order`,
    },
    {
      title: "Free Shipping",
      description: "Offer free shipping on an order",
      icon: "/assets/icons/truck.svg",
      link: `/${storeType}/${storeId}/coupons/free-shipping`,
    },
  ];

  const handleCreateClick = (link: string) => {
    if (link.includes("amount-off-products")) {
      dispatch(resetAmountOffProducts());
    } else if (link.includes("buy-x-get-y")) {
      dispatch(resetBuyXGetY());
    } else if (link.includes("amount-off-order")) {
      dispatch(resetAmountOffOrder());
    } else if (link.includes("free-shipping")) {
      dispatch(resetFreeShipping());
    }
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <ButtonPrimary
          ref={ref}
          className="rounded-full h-full"
          aria-label="Create Discounts"
        >
          <img src="/assets/icons/plus.svg" alt="" width={24} height={24} />
          Create Discounts
        </ButtonPrimary>
      </DialogTrigger>
      <DialogContent
        title=""
        className="w-full min-w-[400px] max-w-2xl rounded-xl bg-secondary-background p-6 pt-4"
      >
        <DialogHeader>
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">
              Select Discount Type
            </h2>
          </div>
        </DialogHeader>
        <div className="space-y-[3px]">
          {discountTypes.map(({ title, description, icon, link }) => (
            <Link
              key={title}
              to={link}
              className="flex items-center gap-2 bg-background rounded-md py-2 px-5"
              aria-label={`Navigate to ${title}`}
              onClick={() => handleCreateClick(link)}
            >
              <div className="flex justify-center items-center shrink-0 h-10 w-10 bg-notification-success/16 rounded-md">
                <img
                  alt={`${title} icon`}
                  src={icon}
                  width={24}
                  height={24}
                />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">{title}</p>
                <p className="font-normal text-xs text-muted-foreground">
                  {description}
                </p>
              </div>
              <div>
                <ArrowUpRight className="w-5 h-5" />
              </div>
            </Link>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
});

CreateDiscounts.displayName = "CreateDiscounts";

export default CreateDiscounts;
